import { Injectable, signal, inject, Injector, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  serverTimestamp,
} from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private injector = inject(Injector);
  private firestore = inject(Firestore);
  private loadingCount = 0;
  loading = signal(false);

  private setLoading(active: boolean): void {
    this.loadingCount = Math.max(0, this.loadingCount + (active ? 1 : -1));
    this.loading.set(this.loadingCount > 0);
  }

  /** Firestore rejects undefined field values on write. */
  private omitUndefined<T extends Record<string, unknown>>(data: T): T {
    return Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== undefined)
    ) as T;
  }

  async get<T>(collectionName: string, id: string): Promise<T | null> {
    this.setLoading(true);
    try {
      return await runInInjectionContext(this.injector, async () => {
        const docRef = doc(this.firestore, collectionName, id);
        const docSnap = await getDoc(docRef);
        return docSnap.exists()
          ? ({ ...docSnap.data(), id: docSnap.id } as T)
          : null;
      });
    } finally {
      this.setLoading(false);
    }
  }

  async create<T>(collectionName: string, data: T): Promise<string> {
    this.setLoading(true);
    try {
      return await runInInjectionContext(this.injector, async () => {
        const collectionRef = collection(this.firestore, collectionName);
        const docRef = await addDoc(collectionRef, {
          ...this.omitUndefined(data as Record<string, unknown>),
          createdAt: serverTimestamp(),
        });
        return docRef.id;
      });
    } finally {
      this.setLoading(false);
    }
  }

  async merge<T>(collectionName: string, id: string, data: T): Promise<void> {
    this.setLoading(true);
    try {
      await runInInjectionContext(this.injector, async () => {
        const docRef = doc(this.firestore, collectionName, id);
        await setDoc(
          docRef,
          {
            ...this.omitUndefined(data as Record<string, unknown>),
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
      });
    } finally {
      this.setLoading(false);
    }
  }

  async update<T>(
    collectionName: string,
    id: string,
    data: Partial<T>
  ): Promise<void> {
    this.setLoading(true);
    try {
      await runInInjectionContext(this.injector, async () => {
        const docRef = doc(this.firestore, collectionName, id);
        await updateDoc(docRef, {
          ...this.omitUndefined(data as Record<string, unknown>),
          updatedAt: serverTimestamp(),
        });
      });
    } finally {
      this.setLoading(false);
    }
  }

  async delete(collectionName: string, id: string): Promise<void> {
    this.setLoading(true);
    try {
      await runInInjectionContext(this.injector, async () => {
        const docRef = doc(this.firestore, collectionName, id);
        await deleteDoc(docRef);
      });
    } finally {
      this.setLoading(false);
    }
  }

  async listOrdered<T>(
    collectionName: string,
    whereClause: { field: string; operator: any; value: any },
    orderField: string,
    orderDirection: 'asc' | 'desc' = 'desc',
    limitCount?: number
  ): Promise<T[]> {
    this.setLoading(true);
    try {
      return await runInInjectionContext(this.injector, async () => {
        const collectionRef = collection(this.firestore, collectionName);
        if (whereClause.value === undefined || whereClause.value === null) {
          return [];
        }
        let q = query(
          collectionRef,
          where(whereClause.field, whereClause.operator, whereClause.value),
          orderBy(orderField, orderDirection)
        );
        if (limitCount) {
          q = query(q, limit(limitCount));
        }
        const snapshot = await getDocs(q);
        return snapshot.docs.map((d) => ({ ...d.data(), id: d.id } as T));
      });
    } finally {
      this.setLoading(false);
    }
  }

  async list<T>(
    collectionName: string,
    whereClause?: { field: string; operator: any; value: any }
  ): Promise<T[]> {
    this.setLoading(true);
    try {
      return await runInInjectionContext(this.injector, async () => {
        const collectionRef = collection(this.firestore, collectionName);
        let q = query(collectionRef);

        if (whereClause) {
          if (whereClause.value === undefined || whereClause.value === null) {
            console.warn(
              `Invalid where clause value for field '${whereClause.field}':`,
              whereClause.value
            );
            return [];
          }
          q = query(
            collectionRef,
            where(whereClause.field, whereClause.operator, whereClause.value)
          );
        }

        const snapshot = await getDocs(q);
        return snapshot.docs.map((d) => ({ ...d.data(), id: d.id } as T));
      });
    } finally {
      this.setLoading(false);
    }
  }
}
